import { useCallback, useRef, useState } from 'react';
import { supabase } from '../config/supabaseClient';
import { ServiceMachineData } from '../components/Agent/ServiceMachineCard';

/**
 * Drives the public-form "verify your phone/email -> autofill your account" flow.
 *
 * Two stateless edge-function calls (public-form-verify):
 *   action 'request' -> sends a 6-digit code, returns { code_id, masked_destination }
 *   action 'check'   -> verifies the code, returns { verified, customer, machines } and, once verified,
 *                       the code's id and the session_secret that binds the code to this browser
 *
 * Modeled on the verification slice of useAgentConversation, but self-contained
 * (no chat conversation state). The edge function always returns HTTP 200 and
 * carries structured errors in `data.code`.
 */

export type VerificationPhase =
  | 'idle'
  | 'sending'
  | 'awaiting_code'
  | 'verifying'
  | 'verified'
  | 'no_match';

export type VerificationChannel = 'sms' | 'email';

export interface VerifiedCustomer {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhone?: string;
  homePhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  preferredContact?: string;
  smsConsent?: boolean;
  customerId?: string;
  customerAddressId?: number | null;
}

const ERROR_COPY: Record<string, string> = {
  rate_limited: 'Too many codes requested. Please wait a minute and try again.',
  send_failed: "We couldn't send the code. Double-check the number or email and try again.",
  expired: 'That code expired. Tap Resend for a new one.',
  max_attempts: 'Too many attempts. Tap Resend for a new code.',
  not_found: 'That code is no longer valid. Tap Resend for a new one.',
  org_mismatch: 'Something went wrong verifying that code. Please try again.',
  request_failed: 'Something went wrong sending the code. Please try again.',
  check_failed: 'Something went wrong verifying that code. Please try again.',
};

function copyFor(code: string | undefined): string {
  return (code && ERROR_COPY[code]) || 'Something went wrong. Please try again.';
}

// Codes that mean the current code is dead but the user can resend a fresh one.
const RESENDABLE = new Set(['expired', 'max_attempts', 'not_found']);

function mapCustomer(c: any): VerifiedCustomer | null {
  if (!c) return null;
  return {
    firstName: c.first_name ?? undefined,
    lastName: c.last_name ?? undefined,
    email: c.email ?? undefined,
    mobilePhone: c.mobile_phone ?? undefined,
    homePhone: c.home_phone ?? undefined,
    address: c.address ?? undefined,
    city: c.city ?? undefined,
    state: c.state ?? undefined,
    zipCode: c.zip_code ?? undefined,
    preferredContact: c.preferred_contact ?? undefined,
    smsConsent: c.sms_consent ?? undefined,
    customerId: c.customer_id ?? undefined,
    customerAddressId: c.customer_address_id ?? null,
  };
}

/** What a verify `check` answer holds once it carries no error code (the edge function puts those in `data.code`). */
export interface CheckPayload {
  verified: boolean;
  codeId: number | null;
  sessionSecret: string | null;
  customer: VerifiedCustomer | null;
  machines: ServiceMachineData[];
}

const field = (data: unknown, key: string): unknown =>
  typeof data === 'object' && data !== null ? (data as Record<string, unknown>)[key] : undefined;

/**
 * Reads a check answer. `verified` is false only when the answer says so ({ verified: false }, a wrong but live code); an answer that
 * says anything else has always been read as verified, and still is. A verified answer carries the code's id and the session_secret
 * that binds the code to this browser (no secret when the server kept none). Pure, and nothing in it is logged.
 */
export function readCheckPayload(data: unknown): CheckPayload {
  const codeId = field(data, 'code_id');
  const secret = field(data, 'session_secret');
  const machines = field(data, 'machines');
  return {
    verified: field(data, 'verified') !== false,
    codeId: typeof codeId === 'number' ? codeId : null,
    sessionSecret: typeof secret === 'string' && secret !== '' ? secret : null,
    customer: mapCustomer(field(data, 'customer')),
    machines: Array.isArray(machines) ? machines : [],
  };
}

export function useFormVerification(organizationId: string) {
  const [phase, setPhase] = useState<VerificationPhase>('idle');
  const [channel, setChannel] = useState<VerificationChannel>('sms');
  const [maskedDestination, setMaskedDestination] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<VerifiedCustomer | null>(null);
  const [machines, setMachines] = useState<ServiceMachineData[]>([]);

  const codeIdRef = useRef<number | null>(null);
  const sessionSecretRef = useRef<string | null>(null);
  const destinationRef = useRef<string>('');

  const invoke = useCallback(
    async (body: Record<string, unknown>) => {
      const { data, error: fnError } = await supabase.functions.invoke('public-form-verify', {
        body: { ...body, organizationId },
      });
      if (fnError) {
        throw new Error(fnError.message || 'Verification request failed');
      }
      return data as any;
    },
    [organizationId],
  );

  const sendCode = useCallback(
    async (ch: VerificationChannel, destination: string) => {
      const dest = destination.trim();
      if (!dest) {
        setError(ch === 'sms' ? 'Enter your phone number first.' : 'Enter your email first.');
        return;
      }
      setError(null);
      setChannel(ch);
      destinationRef.current = dest;
      setPhase('sending');
      try {
        const data = await invoke({ action: 'request', channel: ch, destination: dest });
        if (data?.code) {
          setError(copyFor(data.code));
          setCanResend(data.code !== 'rate_limited');
          setPhase('idle');
          return;
        }
        codeIdRef.current = data?.code_id ?? null;
        sessionSecretRef.current = null; // the secret belongs to the code it came with; the new one has none until it is checked
        setMaskedDestination(data?.masked_destination ?? '');
        setCanResend(true);
        setPhase('awaiting_code');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to send code.');
        setPhase('idle');
      }
    },
    [invoke],
  );

  const requestCode = sendCode;

  const resend = useCallback(() => {
    if (!destinationRef.current) return;
    void sendCode(channel, destinationRef.current);
  }, [sendCode, channel]);

  const submitCode = useCallback(
    async (code: string) => {
      if (codeIdRef.current == null) {
        setError('Please request a new code.');
        return;
      }
      setError(null);
      setPhase('verifying');
      try {
        const data = await invoke({
          action: 'check',
          codeId: codeIdRef.current,
          code,
          channel,
        });

        if (data?.code) {
          // Structured backend error (expired / max_attempts / rate_limited / ...).
          setError(copyFor(data.code));
          setCanResend(RESENDABLE.has(data.code) || data.code !== 'rate_limited');
          setPhase('awaiting_code');
          return;
        }

        const check = readCheckPayload(data);
        if (!check.verified) {
          setError("That code didn't match. Double-check and try again.");
          setPhase('awaiting_code');
          return;
        }

        // Verified. The echoed code id and the session_secret ride in refs beside the id the request gave, for flows that start
        // something with the verified code (the sales shop's session start). Never state, storage or a log.
        if (check.codeId != null) codeIdRef.current = check.codeId;
        sessionSecretRef.current = check.sessionSecret;
        if (!check.customer) {
          setCustomer(null);
          setMachines([]);
          setPhase('no_match');
          return;
        }
        setCustomer(check.customer);
        setMachines(check.machines);
        setPhase('verified');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to verify code.');
        setPhase('awaiting_code');
      }
    },
    [invoke, channel],
  );

  // Takes down the error on screen and nothing else (reset starts the whole verification over).
  const clearError = useCallback(() => setError(null), []);

  const reset = useCallback(() => {
    codeIdRef.current = null;
    sessionSecretRef.current = null;
    destinationRef.current = '';
    setPhase('idle');
    setMaskedDestination('');
    setCanResend(true);
    setError(null);
    setCustomer(null);
    setMachines([]);
  }, []);

  return {
    phase,
    channel,
    maskedDestination,
    canResend,
    error,
    customer,
    machines,
    // Verified-code id, for flows that carry it into follow-up calls (parts
    // widget search/submit). Refreshed on render whenever phase changes.
    codeId: codeIdRef.current,
    // The secret a verified check gave this browser with the code; null until then. Exposed like codeId.
    sessionSecret: sessionSecretRef.current,
    requestCode,
    submitCode,
    resend,
    reset,
    clearError,
  };
}
